import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type Context,
  type ReactNode,
} from 'react';
import { Button, Dialog } from '../ui';
import { useConsoleI18n } from './bind';
import { ErrorBox } from './components';

/**
 * A confirmation for a destructive or costly action: Cancel / confirm in the footer, the confirm button busy while
 * `onConfirm` runs and its error shown inside the dialog (the dialog stays open on failure). A backdrop press never
 * closes it.
 */
export function ConfirmDialog({
  open = true,
  title,
  children,
  confirmLabel,
  cancelLabel,
  danger,
  canConfirm = true,
  onConfirm,
  onClose,
  formatError,
  maxWidth = 560,
  'data-testid': testId,
}: {
  open?: boolean;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** Red confirm button (delete, revoke, overwrite). */
  danger?: boolean;
  canConfirm?: boolean;
  /** Close the dialog yourself when it is done (it stays open on a thrown error). */
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
  /** The error text from what `onConfirm` threw (default: the kit's problem+json aware `ErrorBox`). */
  formatError?: (e: unknown) => string;
  maxWidth?: number;
  'data-testid'?: string;
}) {
  const { t } = useConsoleI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const live = useRef(true);
  useEffect(
    () => () => {
      live.current = false;
    },
    []
  );
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      if (live.current) setError(formatError ? formatError(e) : e);
    } finally {
      if (live.current) setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth={maxWidth}
      closeOnBackdrop={false}
      role="alertdialog"
      data-testid={testId}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={() => void run()}
            loading={busy}
            disabled={!canConfirm}
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      {children && <div className="nyk-confirm-body">{children}</div>}
    </Dialog>
  );
}

export interface ConfirmOptions {
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  danger?: boolean;
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;
// One context per page even if the kit is loaded twice (two bundles, a test that re-imports modules).
const KEY = Symbol.for('nyce.admin-kit.confirm');
const ConfirmContext: Context<Ask | null> = ((globalThis as Record<symbol, unknown>)[KEY] ??=
  createContext<Ask | null>(null)) as Context<Ask | null>;

/**
 * Mount once near the root to get styled confirmations from `useConfirm` and `ConfirmButton`; without it they fall
 * back to the browser's `window.confirm`.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
  const ask = useCallback<Ask>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending((prev) => {
          prev?.resolve(false);
          return { ...options, resolve };
        });
      }),
    []
  );
  const settle = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };
  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {pending && (
        <ConfirmDialog
          title={pending.title}
          confirmLabel={pending.confirmLabel}
          cancelLabel={pending.cancelLabel}
          danger={pending.danger}
          onConfirm={() => settle(true)}
          onClose={() => settle(false)}
          data-testid="confirm-dialog"
        >
          {pending.message}
        </ConfirmDialog>
      )}
    </ConfirmContext.Provider>
  );
}

const plainText = (node: ReactNode) =>
  typeof node === 'string' || typeof node === 'number' ? String(node) : '';

const browserConfirm: Ask = async (o) => {
  const text = [plainText(o.title), plainText(o.message)].filter(Boolean).join('\n\n');
  return typeof window === 'undefined' || typeof window.confirm !== 'function' || window.confirm(text);
};

/** `await confirm({ title, message, danger })` → true when confirmed (styled under `ConfirmProvider`). */
export function useConfirm(): Ask {
  return useContext(ConfirmContext) ?? browserConfirm;
}

/** A button that asks first (`message`), then runs `onConfirm`. */
export function ConfirmButton({
  onConfirm,
  message,
  title,
  confirmLabel,
  children,
  ...rest
}: {
  onConfirm: () => void;
  message: string;
  /** The dialog title under `ConfirmProvider` (default: the message is the title). */
  title?: ReactNode;
  confirmLabel?: ReactNode;
  children: ReactNode;
} & Omit<ComponentProps<typeof Button>, 'onClick'>) {
  const ask = useContext(ConfirmContext);
  return (
    <Button
      {...rest}
      onClick={() => {
        // Without a provider the browser asks, synchronously (as before).
        if (!ask) {
          if (
            typeof window === 'undefined' ||
            typeof window.confirm !== 'function' ||
            window.confirm(message)
          )
            onConfirm();
          return;
        }
        // Without a title the first sentence of the message heads the dialog and the rest goes under it.
        const split = title ? null : /^(.+?[?.!])\s+(\S[\s\S]*)$/.exec(message);
        void ask({
          title: title ?? split?.[1] ?? message,
          message: title ? message : split?.[2],
          confirmLabel: confirmLabel ?? (typeof children === 'string' ? children : undefined),
          danger: rest.variant === 'danger',
        }).then((ok) => ok && onConfirm());
      }}
    >
      {children}
    </Button>
  );
}
